import { useContext, useLayoutEffect, useRef } from 'react';
import cx from 'classnames';

import { makeStyles } from '@mui/styles';
import classnames from 'classnames';
import { useParams } from 'react-router-dom';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import { TOC_TYPES, getCurrentChapter } from '@openclassrooms/ui-web/table-of-content';
import { translate } from '@openclassrooms/translations';
import Typography from '@classify/react-core-components/Typography';
import Box from '@classify/react-core-components/Box';

import CourseContext from '../../contexts/courseContext';
import CourseChapterContext from '../../contexts/courseChapterContext';
import PartTitle from './PartTitle';
import ChapterTocItem from './ChapterTocItem';
import QuizTocItem from './QuizTocItem';
import TocHeader from './TocHeader';
import type { PartType } from '../../types';

export enum TocVariants {
  CHAPTER = 'CHAPTER',
  HOMEPAGE = 'HOMEPAGE',
}

const useStyles = makeStyles(({ spacing, palette, breakpoints }) => ({
  listRoot: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  chapterList: {
    margin: spacing(1, 0, 2, 0),
    padding: 0,
    '& > *': {
      position: 'relative',
      '&::before, &::after': {
        position: 'absolute',
        left: 19,
        height: 8,
        content: '" "',
        borderLeft: `1px solid ${palette.line}`,
      },
      '&::before': {
        top: 0,
        height: 4,
      },
      '&::after': {
        top: 44,
        height: 'calc(100% - 40px)',
      },
      '&:first-of-type::before': {
        top: -8,
        height: 16,
      },
      '&:last-child::after': {
        bottom: -16,
        height: 'calc(100% - 28px)',
      },
    },
  },

  lastPart: {
    '& > *:last-child::after': {
      display: 'none',
    },
  },

  title: {
    borderBottom: `1px solid ${palette.line}`,
    paddingBottom: spacing(1),

    [breakpoints.up('sm')]: {
      marginBottom: spacing(3),
    },
  },

  toc: {
    height: '100%',
    overflow: 'scroll',
    overflowX: 'hidden',
  },

  homepageToc: {
    overflow: 'hidden',
  },
}));

export interface TableOfContentsProps {
  color: string;
  onClose?: () => void;
  onClickItem?: () => void;
  variant?: TocVariants;
  sectionComponent?: any;
}

const TableOfContents = ({
  color,
  onClose = () => {},
  onClickItem = () => {},
  variant = TocVariants.CHAPTER,
  sectionComponent,
}: TableOfContentsProps) => {
  const classes = useStyles();

  const { userToc, flatToc } = useContext(CourseContext);
  const { currentChapterId } = useContext(CourseChapterContext);
  const { exerciseId } = useParams<{ exerciseId: string }>();
  const tocRef = useRef(null);

  const { children: parts } = userToc || { children: [] };

  useLayoutEffect(() => {
    const CHAPTER_HEADER_HEIGHT = 75;
    const currentChapter = getCurrentChapter(
      flatToc,
      exerciseId ? Number(exerciseId) : currentChapterId
    );

    const currentChapterPart = parts.findIndex((part) => part.id === currentChapter?.parentId) + 1;

    const selectedPart = document.querySelector(`#part-${currentChapterPart}`);

    if (tocRef && selectedPart && currentChapterPart > 1) {
      tocRef.current.scrollTop = selectedPart.offsetTop - CHAPTER_HEADER_HEIGHT;
    }
  }, [exerciseId, parts, currentChapterId, flatToc]);

  return (
    <div
      ref={tocRef}
      className={cx(classes.toc, {
        [classes.homepageToc]: variant === TocVariants.HOMEPAGE,
      })}
    >
      {variant === TocVariants.CHAPTER && <TocHeader onClose={onClose} />}
      {variant === TocVariants.HOMEPAGE && (
        <ContentWrapper>
          <Typography className={classes.title} variant="h3" component="h2">
            {translate('course_home.toc.title')}
          </Typography>
        </ContentWrapper>
      )}
      <ul className={classes.listRoot}>
        {parts.map((part, n) => (
          <li key={part.id}>
            <PartTitle color={color} number={n + 1} component={sectionComponent} id={`${n + 1}`}>
              {part.title}
            </PartTitle>
            <Box sx={{ px: 2 }}>
              <ul
                className={classnames(classes.chapterList, {
                  [classes.lastPart]: n === Object.keys(parts).length - 1,
                })}
              >
                {part.children.map((item, i) =>
                  item.type === TOC_TYPES.MCQ ? (
                    <QuizTocItem
                      key={`quiz-${item.id}`}
                      quizPart={item as unknown as PartType}
                      isOnHomePage={variant === TocVariants.HOMEPAGE}
                      onClick={onClickItem}
                    />
                  ) : (
                    <ChapterTocItem
                      key={`chapter-${item.id}`}
                      chapterPart={item as unknown as PartType}
                      number={i + 1}
                      isOnHomePage={variant === TocVariants.HOMEPAGE}
                      onClick={onClickItem}
                    />
                  )
                )}
              </ul>
            </Box>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TableOfContents;
